import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'expo-router';
import { RootState } from '../../redux/store';
import { selectMealsByDate, updateMealRecipe } from '../../redux/mealPlanSlice';
import { selectCookingSession, startSession, resumeSession } from '../../redux/cookingSessionSlice';
import { DeductionSummary } from '../../redux/inventorySlice';
import PlanningView from './PlanningView';
import CookingView from './CookingView';
import DoneView from './DoneView';
import personalRecipeDetails from '../../services/personalRecipeDetails';

interface CookingPlanScreenProps {
  date: string;
}

const CookingPlanScreen: React.FC<CookingPlanScreenProps> = ({ date }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const userId = user?.uid ?? undefined;
  const session = useSelector(selectCookingSession);
  const meals = useSelector((state: RootState) => selectMealsByDate(state, date));
  const dinnerMeals = meals.filter(m => m.mealType === 'dinner' && m.recipe);
  const meal = dinnerMeals[0] ?? null;

  useEffect(() => {
    const mealsNeedingSteps = dinnerMeals.filter(
      m => m.recipe &&
      !m.recipe.instructions?.length &&
      !m.recipe.instructionSections?.length
    );
    if (mealsNeedingSteps.length === 0) return;

    const ids = mealsNeedingSteps.map(m => m.recipeId);
    personalRecipeDetails.getMultipleRecipes(ids).then(recipes => {
      recipes.forEach((recipe, i) => {
        if (!recipe) return;
        dispatch(updateMealRecipe({ recipeId: ids[i], recipe: { ...recipe, id: ids[i] } }));
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!meal) return;
    if (session.mealId === meal.id && session.screen !== 'done') {
      dispatch(resumeSession({ mealId: meal.id, date }));
    } else {
      dispatch(startSession({ mealId: meal.id, date }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meal?.id, date]);

  const [deductionSummary, setDeductionSummary] = useState<DeductionSummary[] | null>(null);

  if (!meal) {
    return null;
  }

  if (session.screen === 'planning') {
    return <PlanningView meal={meal} />;
  }

  if (session.screen === 'cooking') {
    return <CookingView meal={meal} userId={userId} onDone={(s) => setDeductionSummary(s)} />;
  }

  return <DoneView meal={meal} userId={userId} deductionSummary={deductionSummary} />;
};

export default CookingPlanScreen;
